/**
 * Icon — the shared monochrome inline-SVG icon set (T27.2.5.2).
 *
 * One tiny primitive that replaces every emoji + text-glyph affordance in app
 * chrome (C2, C3 from the UXA assessment). Each icon is a 24px-viewBox outline
 * drawn with `stroke="currentColor"` (a few solid shapes use `fill`), so it:
 *
 *   - renders identically across devices (no per-vendor emoji font), and
 *   - inherits color from its context — it themes to dark automatically and
 *     takes the accent in the active bottom-nav state (the tab's active color
 *     cascades through `currentColor`), with no per-icon color prop.
 *
 * Sizing: the SVG is `1em` square by default, so it inherits the font-size of
 * its slot (set `fontSize` on the parent, as the old emoji spans did). Pass a
 * `size` prop (a token-backed length or a number-of-rem) only when a fixed size
 * is wanted.
 *
 * Accessibility: decorative by default (`aria-hidden`), so an icon inside a
 * labelled button never double-announces. Pass `title` to give a standalone
 * icon an accessible name (it then becomes `role="img"`, not hidden).
 *
 * Tokens-only (CP5): the set uses ONLY `currentColor` / `none` for paint and
 * unitless geometry — no hex, no `px`. token-scan stays clean.
 */

import type { CSSProperties, ReactNode } from 'react';

// Every chrome glyph this set replaces. Adding a name = adding a `<path>` entry
// to PATHS below; nothing else changes at the call sites.
export type IconName =
  | 'list'
  | 'compass'
  | 'tag'
  | 'card'
  | 'cart'
  | 'users'
  | 'home'
  | 'bookmark'
  | 'pin'
  | 'box'
  | 'gear'
  | 'logout'
  | 'chevron-right'
  | 'menu'
  | 'arrow-left'
  | 'plus'
  | 'mic'
  // Settings-destination affordances (T27.2.7.6 / MN5) — extend the set to the
  // surfaces T27.2.5.2 deliberately left (preference chips, the store-row
  // cluster, the member thumb). Star has an outline + a filled variant so the
  // favourite control reads on/off without a colour-only signal.
  | 'star'
  | 'star-filled'
  | 'pencil'
  | 'close'
  | 'ban'
  | 'more'
  | 'check'
  | 'person'
  // 💬 Chat — the Feedback destination (T35.3): a speech bubble.
  | 'chat'
  // 👁 Eye / eye-off — the password reveal toggle. `eye` = currently masked
  // (tap to show); `eye-off` = currently revealed (tap to hide).
  | 'eye'
  | 'eye-off';

export interface IconProps {
  /** Which icon to draw (see the emoji→icon map in T27.2.5.2). */
  name: IconName;
  /**
   * Optional fixed size. A token-backed length (`var(--font-size-xl)`) or a
   * rem string. Omit to inherit the slot's font-size (`1em`) — the default and
   * the path that lets the active-nav accent tint the icon for free.
   */
  size?: string;
  /**
   * Accessible name for a STANDALONE icon (no labelled button around it). When
   * set, the icon is announced (`role="img"`); otherwise it is decorative
   * (`aria-hidden`).
   */
  title?: string;
  /** Forwarded for feature tests / styling hooks. */
  'data-testid'?: string;
  className?: string;
  style?: CSSProperties;
}

// Geometry per icon. Each entry is the inner `<svg>` content for a `0 0 24 24`
// viewBox. Outline icons stroke `currentColor` at a consistent 2-unit weight;
// a few solid marks (the chevron/arrow heads, the bookmark) use `fill`. No hex,
// no px — only `currentColor` / `none`.
const PATHS: Record<IconName, ReactNode> = {
  // 📝 List — lines of a checklist.
  list: (
    <>
      <line x1="9" y1="7" x2="20" y2="7" />
      <line x1="9" y1="12" x2="20" y2="12" />
      <line x1="9" y1="17" x2="20" y2="17" />
      <path d="M4 6.5l1.2 1.2L7.4 5.3" />
      <path d="M4 11.5l1.2 1.2L7.4 10.3" />
      <path d="M4 16.5l1.2 1.2L7.4 15.3" />
    </>
  ),
  // 🧭 Compass / Trip + Planning — ring with a needle.
  compass: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M15.5 8.5l-2 5-5 2 2-5 5-2z" />
    </>
  ),
  // 🏷️ Tag (Deals) — a price tag with its eyelet.
  tag: (
    <>
      <path d="M12.6 3H5a2 2 0 00-2 2v7.6a2 2 0 00.6 1.4l6.4 6.4a2 2 0 002.8 0l5.6-5.6a2 2 0 000-2.8L12 3.6A2 2 0 0010.6 3z" />
      <circle cx="7.5" cy="7.5" r="1.4" fill="currentColor" stroke="none" />
    </>
  ),
  // 💳 Card — payment card with a stripe.
  card: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="7" y1="15" x2="11" y2="15" />
    </>
  ),
  // 🛒 Cart (Shopping) — trolley with two wheels.
  cart: (
    <>
      <path d="M3 4h2l2.2 11.2a1.5 1.5 0 001.5 1.2h8.1a1.5 1.5 0 001.5-1.2L20 8H6" />
      <circle cx="9.5" cy="20" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="17.5" cy="20" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
  // 👥 Users — two member silhouettes.
  users: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19a5.5 5.5 0 0111 0" />
      <path d="M16 5.2a3.2 3.2 0 010 5.8" />
      <path d="M16.5 13.4A5.5 5.5 0 0120.5 19" />
    </>
  ),
  // 🏠 Home — house with a door.
  home: (
    <>
      <path d="M4 11l8-6.5L20 11" />
      <path d="M6 10v9h12v-9" />
      <path d="M10.5 19v-4.5h3V19" />
    </>
  ),
  // 🏷️ Bookmark (Brand prefs — distinct from Deals `tag`) — a saved ribbon.
  bookmark: (
    <path d="M7 3.5h10a1 1 0 011 1V20l-6-3.4L6 20V4.5a1 1 0 011-1z" />
  ),
  // 📍 Pin — map marker.
  pin: (
    <>
      <path d="M12 21s7-6.3 7-11.5A7 7 0 005 9.5C5 14.7 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.4" />
    </>
  ),
  // 📦 Box — parcel with a lid seam.
  box: (
    <>
      <path d="M3.5 7.5L12 3l8.5 4.5v9L12 21l-8.5-4.5v-9z" />
      <path d="M3.5 7.5L12 12l8.5-4.5" />
      <line x1="12" y1="12" x2="12" y2="21" />
    </>
  ),
  // ⚙️ Gear — App settings.
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v3M12 18.5v3M21.5 12h-3M5.5 12h-3M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1M18.7 18.7l-2.1-2.1M7.4 7.4L5.3 5.3" />
    </>
  ),
  // ⎋ Logout — door with an out-arrow.
  logout: (
    <>
      <path d="M14 4.5H6.5a2 2 0 00-2 2v11a2 2 0 002 2H14" />
      <path d="M18 8.5l3.5 3.5L18 15.5" />
      <line x1="21" y1="12" x2="10" y2="12" />
    </>
  ),
  // › Chevron-right — row affordance.
  'chevron-right': <path d="M9.5 6l6 6-6 6" />,
  // ☰ Menu — three bars.
  menu: (
    <>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </>
  ),
  // ‹ Arrow-left — Back.
  'arrow-left': (
    <>
      <line x1="20" y1="12" x2="5" y2="12" />
      <path d="M11 6l-6 6 6 6" />
    </>
  ),
  // ＋ Plus — quick add.
  plus: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </>
  ),
  // 🎤 Mic — voice capture.
  mic: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M6 11a6 6 0 0012 0" />
      <line x1="12" y1="17" x2="12" y2="21" />
      <line x1="9" y1="21" x2="15" y2="21" />
    </>
  ),
  // ☆ Star (outline) — an unset favourite.
  star: (
    <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 17l-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z" />
  ),
  // ★ Star (filled) — a set favourite / a liked preference.
  'star-filled': (
    <path
      d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 17l-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z"
      fill="currentColor"
      stroke="none"
    />
  ),
  // ✎ Pencil — edit a store.
  pencil: (
    <>
      <path d="M4 16.5V20h3.5L18 9.5 14.5 6z" />
      <path d="M13 7.5L16.5 11" />
    </>
  ),
  // ✕ Close — remove a row / dismiss a chip.
  close: (
    <>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </>
  ),
  // 🚫 Ban — an avoided preference (never a hard hide).
  ban: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </>
  ),
  // ⋯ More — opens the row's overflow action menu.
  more: (
    <>
      <circle cx="6" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="18" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </>
  ),
  // ✓ Check — a toggled-on category chip.
  check: <path d="M5 12.5l4 4L19 6.5" />,
  // 🧑 Person — a single member silhouette (the member-row thumb).
  person: (
    <>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5.5 19.5a6.5 6.5 0 0113 0" />
    </>
  ),
  // 💬 Chat (Feedback) — a rounded speech bubble with a tail.
  chat: (
    <path d="M4 5.5h16a1 1 0 011 1V16a1 1 0 01-1 1H9l-4 3.5V17H4a1 1 0 01-1-1V6.5a1 1 0 011-1z" />
  ),
  // 👁 Eye (masked → tap to show) — an outline eye with a pupil.
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  // 👁‍🗨 Eye-off (revealed → tap to hide) — the same eye with a slash.
  'eye-off': (
    <>
      <path d="M2.5 12S6 5.5 12 5.5c1.8 0 3.4.6 4.7 1.4M21.5 12s-1 1.9-3 3.6M9.5 9.7A3 3 0 0014.3 14.3" />
      <path d="M6.7 6.7C4 8.3 2.5 12 2.5 12S6 18.5 12 18.5c1.7 0 3.2-.5 4.5-1.2" />
      <line x1="4" y1="4" x2="20" y2="20" />
    </>
  ),
};

export function Icon({
  name,
  size,
  title,
  'data-testid': dataTestId,
  className,
  style,
}: IconProps): JSX.Element {
  const decorative = title === undefined;
  const dimension = size ?? '1em';

  return (
    <svg
      viewBox="0 0 24 24"
      width={dimension}
      height={dimension}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      // Decorative by default so an icon inside a labelled button never
      // double-announces; labelled when a `title` is given (standalone use).
      aria-hidden={decorative ? true : undefined}
      role={decorative ? undefined : 'img'}
      aria-label={title}
      focusable="false"
      data-testid={dataTestId}
      data-icon={name}
      className={className}
      style={{ display: 'inline-block', flex: '0 0 auto', ...style }}
    >
      {PATHS[name]}
    </svg>
  );
}
